import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import {
  DefaultApiClient,
  DefaultApiResponse,
} from '@internal/backstage-plugin-devops-react';
import {
  ExtendedRbacPolicy,
  ExtendedRbacRole,
  ExtendedRbacRoleVersion,
  RbacMember,
  RbacPermissionsMetadata,
  RbacPolicy,
  RbacRole,
} from '@internal/backstage-plugin-devops-rbac-common';
import { RbacApi } from './RbacApi';

export class RbacApiClient extends DefaultApiClient implements RbacApi {
  public constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    super({
      backendName: 'permission',
      ...options,
    });
  }

  async getRoles(): Promise<ExtendedRbacRole[]> {
    const urlSegment = `v2/role`;
    const response = await this.get(urlSegment);
    return response.body.items;
  }

  async getRole(roleId: string): Promise<ExtendedRbacRole> {
    const urlSegment = `v2/role/${roleId}`;
    const response = await this.get(urlSegment);
    return response.body.item;
  }

  async createRole(data: RbacRole): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role`;
    return await this.post<RbacRole>(urlSegment, data);
  }

  async editRole(id: string, data: RbacRole): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${id}`;
    return await this.put<RbacRole>(urlSegment, data);
  }

  async deleteRole(id: string): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${id}`;
    return await this.delete(urlSegment);
  }

  async getMembers(
    roleId: string,
    roleVersionId: string,
  ): Promise<RbacMember[]> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/member`;
    const response = await this.get(urlSegment);
    return response.body.items;
  }

  async addMembers(
    roleId: string,
    roleVersionId: string,
    membersEntityRefs: string[],
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}`;
    return await this.put(urlSegment, { membersEntityRefs });
  }

  async deleteMember(
    roleId: string,
    roleVersionId: string,
    memberId: string,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/member/${memberId}`;
    return await this.delete(urlSegment);
  }

  async getPolicies(
    roleId: string,
    roleVersionId: string,
  ): Promise<RbacPolicy[]> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/policy`;
    const response = await this.get(urlSegment);
    return response.body.items;
  }

  async addPolicy(
    roleId: string,
    roleVersionId: string,
    data: ExtendedRbacPolicy,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/policy`;
    return await this.post<ExtendedRbacPolicy>(urlSegment, data);
  }

  async editPolicy(
    roleId: string,
    roleVersionId: string,
    policyId: string,
    data: ExtendedRbacPolicy,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/policy/${policyId}`;
    return await this.put<ExtendedRbacPolicy>(urlSegment, data);
  }

  async deletePolicy(
    roleId: string,
    roleVersionId: string,
    policyId: string,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/policy/${policyId}`;
    return await this.delete(urlSegment);
  }

  async reorderPolicy(
    roleId: string,
    roleVersionId: string,
    policyId: string,
    reorderDirection: number,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/policy/${policyId}/reorder`;
    return await this.put(urlSegment, { reorderDirection });
  }

  async getPermissions(): Promise<RbacPermissionsMetadata[]> {
    const urlSegment = `v2/rbac/permission`;
    const response = await this.get(urlSegment);
    return response.body.items;
  }

  async getRoleVersion(
    roleId: string,
    roleVersionId: string,
  ): Promise<ExtendedRbacRoleVersion> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}`;
    const response = await this.get(urlSegment);
    return response.body.item;
  }

  async createNewVersionOfRole(
    roleId: string,
    roleVersionToCopyId: string,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/roleVersion`;
    return await this.post(urlSegment, { roleVersionToCopyId });
  }

  async activateRoleVersion(
    roleId: string,
    roleVersionId: string,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}/activate`;
    return await this.put(urlSegment, {});
  }

  async deleteRoleVersion(
    roleId: string,
    roleVersionId: string,
  ): Promise<DefaultApiResponse> {
    const urlSegment = `v2/role/${roleId}/${roleVersionId}`;
    return await this.delete(urlSegment);
  }
}
